import { Button, Icon, Skeleton, Stack } from '@hurdl/design-system';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  Check,
  ClipboardList,
  UtensilsCrossed,
} from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import {
  FAST_FOOD_PRODUCT_KEY,
  FAST_FOOD_RESTAURANT_KEY,
  useMenuOperations,
} from './menus-wiring.js';
import { Tile, TileHead, TileStat, TileStats } from './tile.js';

/**
 * The Admin_Panel home, a triage board: at a glance, which Products need the Operator's
 * attention right now, then a tile per Product leading with the work waiting on it and a
 * direct way to start (admin-panel R2.8).
 *
 * ── Built to scale, and to be read at any hour ───────────────────────────────
 *
 * The panel is one surface over many Products — today one, in time around forty — so the
 * summary is written for the many, not the one. It does not total items ("N items this
 * morning"); a global count across forty Products says nothing about where to look.
 * Instead it says how many Products need attention and names them, so the Operator sees
 * *which* applications have overnight work and can open the ones that matter. With one
 * Product it reads naturally; with forty it still fits on a line and the tiles below —
 * sorted so the ones with work come first — are the detail.
 *
 * The greeting follows the clock, not a fixed "morning": someone opens this from bed at
 * ten and from their desk at six, and a frozen "Good morning" at night is wrong. The
 * hour is read locally and resolves to morning / afternoon / evening / night.
 *
 * The dashboard surveys the Products this deployment declares — one tile each — not a
 * fixed set of capabilities. The one landed Product, UK Fast Food Menus, is the one tile
 * today; a second is a new entry in the Products survey, not a code change to this board.
 *
 * The one `h1` is the page's; the `main` landmark is the Layout's. The tiles' links
 * resolve against the route tree, so no address literal reaches this source.
 */
export function DashboardPage(): ReactElement {
  const counts = useFastFoodCounts();

  // The Products the board surveys, each with its open-work count. The summary reads this
  // to total and name the ones needing attention, and the tiles render from it. One
  // Product today; the shape is the list it will be with many.
  const products: ReadonlyArray<ProductSummary> = [
    {
      key: FAST_FOOD_PRODUCT_KEY,
      label: 'UK Fast Food Menus',
      toReview: counts?.toReview,
    },
  ];

  return (
    <Stack gap="large">
      <AttentionSummary products={products} loading={counts === undefined} />

      <div className="grid gap-hurdl-5 md:grid-cols-2">
        <FastFoodTile counts={counts} />
      </div>
    </Stack>
  );
}

/** What the board knows about one Product for the summary: its name and its open work. */
interface ProductSummary {
  readonly key: string;
  readonly label: string;
  /** Items awaiting review, or `undefined` while the Product's counts load. */
  readonly toReview: number | undefined;
}

/** The counts the dashboard loads for the fast-food Product. */
interface Counts {
  readonly toReview: number;
  readonly reviewed: number;
}

/**
 * Loads the fast-food Product's review and published counts once, for the whole page,
 * so the summary and the tile read the same figures from one load. `undefined` while in
 * flight, which each consumer renders as its own placeholder.
 */
function useFastFoodCounts(): Counts | undefined {
  const operations = useMenuOperations();
  const [counts, setCounts] = useState<Counts | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void Promise.all([
      operations.listQueue(),
      operations.listReviewed(FAST_FOOD_RESTAURANT_KEY),
    ]).then(([queue, reviewed]) => {
      if (live)
        setCounts({ toReview: queue.length, reviewed: reviewed.length });
    });
    return () => {
      live = false;
    };
  }, [operations]);

  return counts;
}

/** The part of the day, from the local hour, for a greeting that is not frozen in time. */
function partOfDay(hour: number): string {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'night';
}

/**
 * The board's verdict, built to be read at a glance and to scale to many Products: a
 * greeting that follows the clock, a headline that leads with the *number* of Products
 * needing attention, and then those Products named as links straight to their work.
 *
 * The greeting is "Good morning/afternoon/evening", or "Working late" after hours — the
 * hour read locally so it is never a frozen "morning". Beneath it the headline emphasises
 * the figure itself — "**3** products need your attention" — because the count is the
 * number the Operator came for, and keeps that count visible however many Products there
 * are. The Products needing work are then a wrapping row of links, each going to that
 * Product's review queue, so the Operator reads *which* applications need them and clicks
 * straight through — the design that still works at forty Products, where a run-on
 * sentence would not. A clear board resolves to a quiet confirmation with a check.
 */
function AttentionSummary({
  products,
  loading,
}: {
  readonly products: ReadonlyArray<ProductSummary>;
  readonly loading: boolean;
}): ReactElement {
  const hour = new Date().getHours();
  const part = partOfDay(hour);
  const greeting = part === 'night' ? 'Working late' : `Good ${part}`;

  const needing = products.filter((product) => (product.toReview ?? 0) > 0);

  return (
    <Stack gap="medium">
      <h1 className="font-hurdl-display text-hurdl-display font-hurdl-semibold">
        {greeting}
      </h1>

      {loading ? (
        <Skeleton className="h-hurdl-8 w-80" />
      ) : needing.length === 0 ? (
        <Stack direction="horizontal" gap="small" align="centre">
          <span className="flex size-hurdl-6 shrink-0 items-center justify-center rounded-full bg-hurdl-success-background text-hurdl-success-foreground">
            <Icon icon={Check} size="small" />
          </span>
          <p className="text-hurdl-subheading text-hurdl-muted-foreground">
            Every product is clear — nothing waiting on you.
          </p>
        </Stack>
      ) : (
        <Stack gap="small">
          <p className="text-hurdl-subheading text-card-foreground">
            <span className="font-hurdl-display text-hurdl-heading font-hurdl-semibold text-hurdl-primary-background">
              {needing.length}
            </span>{' '}
            {needing.length === 1 ? 'product needs' : 'products need'} your
            attention
          </p>
          {/* The applications needing work, each a link straight to its review queue —
              a wrapping row so it reads at a glance and still fits at forty Products. */}
          <div className="flex flex-wrap gap-hurdl-2">
            {needing.map((product) => (
              <Link
                key={product.key}
                to="/products/$productKey/review"
                params={{ productKey: product.key }}
                className="group inline-flex items-center gap-hurdl-2 rounded-hurdl-medium border border-border bg-muted/70 px-hurdl-3 py-hurdl-1 text-hurdl-body font-hurdl-semibold text-card-foreground transition-colors duration-(--duration-hurdl-medium) hover:border-ring hover:text-hurdl-primary-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {product.label}
                <span className="text-hurdl-caption font-hurdl-regular text-hurdl-muted-foreground">
                  {product.toReview}
                </span>
                <ArrowRight
                  aria-hidden="true"
                  className="size-hurdl-4 shrink-0 transition-transform duration-(--duration-hurdl-medium) ease-(--ease-hurdl-spring) group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                />
              </Link>
            ))}
          </div>
        </Stack>
      )}
    </Stack>
  );
}

/**
 * The fast-food Product tile: the review work it carries as the emphasised figure, the
 * published total beside it, and the two ways in — Review now (primary, the job) and
 * Restaurants (secondary, its arrow the one microinteraction). Built on the shared
 * {@link Tile} so it stays aligned with the Restaurants and hub boards.
 */
function FastFoodTile({
  counts,
}: {
  readonly counts: Counts | undefined;
}): ReactElement {
  const waiting = (counts?.toReview ?? 0) > 0;

  return (
    <Tile>
      <TileHead
        icon={UtensilsCrossed}
        title="UK Fast Food Menus"
        description="Review extracted items and keep the published menus right."
      />

      <TileStats>
        <TileStat
          label="To review"
          value={counts?.toReview}
          emphasis={waiting}
        />
        <TileStat label="Published" value={counts?.reviewed} />
      </TileStats>

      <Stack
        direction="responsive"
        gap="small"
        className="w-full sm:items-center"
      >
        <Button
          variant={waiting ? 'primary' : 'secondary'}
          asChild
          className="w-full sm:w-auto"
        >
          <Link
            to="/products/$productKey/review"
            params={{ productKey: FAST_FOOD_PRODUCT_KEY }}
          >
            <Icon icon={ClipboardList} size="small" />
            {waiting ? 'Review now' : 'Review queue'}
          </Link>
        </Button>

        <Button variant="secondary" asChild className="group w-full sm:w-auto">
          <Link
            to="/products/$productKey/menus"
            params={{ productKey: FAST_FOOD_PRODUCT_KEY }}
          >
            Restaurants
            <ArrowRight
              aria-hidden="true"
              className="size-hurdl-4 shrink-0 transition-transform duration-(--duration-hurdl-medium) ease-(--ease-hurdl-spring) group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
            />
          </Link>
        </Button>
      </Stack>
    </Tile>
  );
}
